'use client'

import { createContext, createElement, useContext, useMemo, type ReactNode } from 'react'
import { useAuth } from '@clerk/nextjs'
import { useActiveSchoolId } from '@/components/context/school-context'
import { createBackendClient, type BackendClient } from './client'
import { fetchTransport } from './transport'

const BackendClientContext = createContext<BackendClient | null>(null)

/**
 * Overrides the client for a subtree: tests and harnesses mount it with
 * `createTestBackendClient().client`. Production mounts none and
 * `useBackendClient` builds the client from the Clerk session.
 */
export function BackendClientProvider({ client, children }: { client: BackendClient; children?: ReactNode }) {
  return createElement(BackendClientContext.Provider, { value: client }, children)
}

/**
 * The backend client for client components, bound to the Clerk session and
 * the active school. A school switch returns a new client; a client already
 * handed to an in-flight query keeps sending the school it was built with.
 * Key queries on `client.schoolId`.
 */
export function useBackendClient(): BackendClient {
  const override = useContext(BackendClientContext)
  // A BackendClientProvider wraps a whole tree or none of it, so this branch
  // never changes between renders of one component.
  return override ?? useSessionBackendClient()
}

function useSessionBackendClient(): BackendClient {
  const { getToken } = useAuth()
  // Falls back to the active-school cookie before SchoolProvider has set the
  // school; without that the first render's queries go out with no
  // `x-school-id`, get a 428, and stay cached in error under a key that never
  // changes.
  const schoolId = useActiveSchoolId()

  return useMemo(
    () => createBackendClient({ transport: fetchTransport, getToken: () => getToken(), schoolId }),
    [getToken, schoolId]
  )
}
